"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn, sectionBackgroundColors, spacingClasses } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import Link from "next/link";
import Image from '@/components/ui/Image';
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { useVideo, VideoIframe } from "../components/VideoIframe";

interface Hero21Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string; 
    image?: string;
    imageAltText?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
    video?: string;
    videoThumbnail?: string;
    videoThumbnailAltText?: string;
    videoHelperText?: string;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero21 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: Hero21Props) => {
  const spacingClass = spacingClasses[spacing || 'none'];

  const { isVideoOpen, currentVideo, getVideoThumbnail, handleVideoClick, handleCloseVideo } = useVideo();
  
  const heroClass = cn(
    spacingClass,
    sectionBackgroundColors(darkMode, variant),
  );

  return (
    <SectionWrapper
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero21"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 21"
      className="!p-0 !bg-transparent"
    > 
    
      <div 
        className={heroClass}
      >
        <div className="container"> 
          <div className="flex flex-col lg:flex-row gap-20 lg:gap-24">
            <div className="w-full lg:w-12/24 flex flex-col gap-10 lg:gap-40 items-center lg:items-start">
              <div className="flex flex-col gap-6 text-center lg:text-left">
                {contents?.badge && (
                  <Badge variant="outline" bold className="mx-auto lg:mx-0">
                    {contents?.badge}
                  </Badge>
                )}
                {contents?.title && (
                  <h1>
                    {parse(contents.title)}
                  </h1>
                )}
                {contents?.content && (
                  <div className="mt-4 paragraph-lg">
                    {parse(contents?.content)}
                  </div>
                )}
                {contents?.hasButton && (
                  <div className="mt-2">
                    <div className="flex flex-col items-center gap-4 lg:flex-row">
                      <Button
                        asChild
                        size='lg'
                        className="w-full sm:w-auto"
                        variant={contents?.buttonStyle || "primary"}
                      >
                        <Link href={contents?.buttonLink ?? ""}>{contents?.buttonText}</Link>
                      </Button>
                      {contents?.secondaryButton && (
                        <Button
                          className="w-full sm:w-auto"
                          variant={contents?.secondaryButtonStyle || "outline"}
                          asChild
                          size='lg'
                        >
                          <Link href={contents?.secondaryButtonLink ?? ""}> 
                            {contents?.secondaryButtonText}
                          </Link>
                        </Button>
                      )}
                    </div>
                    {contents?.microCopy && (
                      <p className="mt-2 paragraph-sm text-muted-foreground">
                        {contents?.microCopy}
                      </p>
                    )}
                  </div>
                )}
              </div>
              {contents?.video && (
                <div 
                  className="flex flex-col items-start" 
                  onClick={() => handleVideoClick(contents?.video || "")}
                > 
                  <div className="relative cursor-pointer aspect-[16/9] rounded-xl overflow-hidden lg:max-w-[245px]">
                    <Image
                      src={contents?.videoThumbnail || getVideoThumbnail(contents?.video || "") || '/placeholder.svg'}
                      alt={contents?.videoThumbnailAltText || "Video thumbnail"}
                      className="h-full w-full object-cover aspect-[16/9] rounded-xl"
                      width={300}
                      height={200}
                      priority
                      fetchPriority="high"
                    />
                    <div className="absolute flex left-[16px] bottom-[16px] w-11 h-11">
                      <Button 
                        variant="secondary" 
                        size="iconLg"   
                        className="border-none bg-muted rounded-full w-11 h-11"
                      >
                        <span className="material-symbols-outlined font-variation-filled">play_arrow</span>
                      </Button>
                    </div>
                    <VideoIframe 
                      videoUrl={currentVideo} 
                      isOpen={isVideoOpen} 
                      onClose={handleCloseVideo} 
                    />
                  </div>
                  {contents?.videoHelperText && (
                    <p className="mt-2 paragraph-sm text-muted-foreground">
                      {contents?.videoHelperText}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="w-full hidden lg:w-12/24 lg:flex items-center justify-center rounded-xl sm:rounded-2xl xl:rounded-3xl">
              {contents?.image && (
                <Image
                  src={contents?.image}
                  alt={contents?.imageAltText || ""}
                  title={contents?.imageAltText || ""}
                  width={688}
                  height={521}
                  className="object-cover"
                  priority
                  fetchPriority="high"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Hero21 };
